import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import {
  Upload,
  FileText,
  Loader2,
  CheckCircle,
  AlertTriangle,
  ChevronRight,
  Shield,
  Zap,
  X,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AnalyserPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [cvText, setCvText] = useState('');
  const [cvFile, setCvFile] = useState(null);
  const [offerText, setOfferText] = useState('');
  const [cvMode, setCvMode] = useState('file'); // 'file' | 'text'
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1); // 1: CV, 2: offer
  const cvInputRef = useRef(null);

  const PROGRESS_MSGS = [
    'Lecture de votre CV...',
    'Comparaison avec l\'offre...',
    'Calcul du score ATS...',
    'Finalisation de l\'analyse...',
  ];

  useEffect(() => {
    if (!loading) { setProgress(0); return; }
    setProgress(2);
    let p = 2;
    const id = setInterval(() => {
      p = p + (99 - p) * 0.07 + 0.4;
      if (p > 99) p = 99;
      setProgress(Math.round(p));
    }, 350);
    return () => clearInterval(id);
  }, [loading]);

  const handleCvFile = async (file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!name.endsWith('.pdf') && !name.endsWith('.docx')) {
      setError('Format non supporté. Utilisez PDF ou DOCX.');
      return;
    }
    setCvFile(file);
    setError('');
    setExtracting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/documents/extract`, form, {
        withCredentials: true,
      });
      setCvText(data.text);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'extraction du fichier.");
      setCvFile(null);
    } finally {
      setExtracting(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleCvFile(file);
  };

  const handleAnalyse = async () => {
    if (!cvText.trim()) {
      setError('Ajoutez votre CV pour lancer l\'analyse.');
      return;
    }
    if (!offerText.trim()) {
      setError('Collez le texte de l\'offre d\'emploi.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      if (user) {
        // Logged-in flow: use regular auth endpoint
        const { data } = await axios.post(
          `${API_URL}/api/analysis/create`,
          { cv_text: cvText, job_offer_text: offerText },
          { withCredentials: true }
        );
        navigate(`/resultats/${data.id}`);
      } else {
        // Anonymous flow
        const { data } = await axios.post(
          `${API_URL}/api/analysis/create-anonymous`,
          { cv_text: cvText, job_offer_text: offerText }
        );
        // Store token in localStorage for 48h
        localStorage.setItem(`analysis_token_${data.id}`, data.session_token);
        navigate(`/resultats/${data.id}?token=${data.session_token}`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'analyse.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Header />

      <div className="pt-20 pb-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">

          {/* Title */}
          <div className="text-center pt-8 pb-6">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 text-xs font-bold px-3 py-1.5 rounded-full mb-4">
              <Zap className="h-3.5 w-3.5" />
              RÉSULTAT EN 30 SECONDES
            </div>
            <h1
              className="text-2xl sm:text-3xl font-bold text-zinc-900 leading-tight mb-2"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Analysez votre CV
            </h1>
            <p className="text-zinc-500 text-sm">
              Sans inscription — paiement uniquement si vous voulez le rapport complet
            </p>
          </div>

          {/* Progress steps */}
          <div className="flex items-center gap-2 mb-6">
            <div className={`flex-1 flex items-center gap-2 p-3 rounded-xl border-2 transition-colors ${step === 1 ? 'border-blue-600 bg-blue-50' : 'border-zinc-200 bg-white'}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${cvText ? 'bg-green-500 text-white' : step === 1 ? 'bg-blue-600 text-white' : 'bg-zinc-200 text-zinc-500'}`}>
                {cvText ? <CheckCircle className="h-4 w-4" /> : '1'}
              </div>
              <span className="text-sm font-medium text-zinc-700">Votre CV</span>
            </div>
            <ChevronRight className="h-4 w-4 text-zinc-300 flex-shrink-0" />
            <div className={`flex-1 flex items-center gap-2 p-3 rounded-xl border-2 transition-colors ${step === 2 ? 'border-blue-600 bg-blue-50' : 'border-zinc-200 bg-white'}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${offerText ? 'bg-green-500 text-white' : step === 2 ? 'bg-blue-600 text-white' : 'bg-zinc-200 text-zinc-500'}`}>
                {offerText ? <CheckCircle className="h-4 w-4" /> : '2'}
              </div>
              <span className="text-sm font-medium text-zinc-700">L'offre</span>
            </div>
          </div>

          {/* Step 1: CV */}
          {step === 1 && (
            <div className="bg-white border border-zinc-200 rounded-2xl p-5 mb-4">
              <h2 className="font-semibold text-zinc-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Votre CV
              </h2>

              {/* Mode toggle */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setCvMode('file')}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${cvMode === 'file' ? 'bg-blue-600 text-white' : 'bg-zinc-100 text-zinc-600'}`}
                >
                  Importer un fichier
                </button>
                <button
                  onClick={() => setCvMode('text')}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${cvMode === 'text' ? 'bg-blue-600 text-white' : 'bg-zinc-100 text-zinc-600'}`}
                >
                  Coller le texte
                </button>
              </div>

              {cvMode === 'file' ? (
                <div>
                  {cvText ? (
                    <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-green-800 truncate">{cvFile?.name || 'CV importé'}</p>
                        <p className="text-xs text-green-600">{cvText.length} caractères extraits</p>
                      </div>
                      <button
                        onClick={() => { setCvText(''); setCvFile(null); }}
                        className="text-zinc-400 hover:text-zinc-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div
                      onDrop={handleDrop}
                      onDragOver={(e) => e.preventDefault()}
                      onClick={() => cvInputRef.current?.click()}
                      className="border-2 border-dashed border-zinc-300 rounded-xl p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
                    >
                      {extracting ? (
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
                          <p className="text-sm text-zinc-500">Extraction en cours...</p>
                        </div>
                      ) : (
                        <>
                          <Upload className="h-8 w-8 text-zinc-400 mx-auto mb-2" />
                          <p className="text-sm font-medium text-zinc-700">Glissez votre CV ici</p>
                          <p className="text-xs text-zinc-400 mt-1">ou cliquez pour sélectionner (PDF, DOCX)</p>
                        </>
                      )}
                    </div>
                  )}
                  <input
                    ref={cvInputRef}
                    type="file"
                    accept=".pdf,.docx"
                    className="hidden"
                    onChange={(e) => handleCvFile(e.target.files[0])}
                  />
                </div>
              ) : (
                <textarea
                  value={cvText}
                  onChange={(e) => setCvText(e.target.value)}
                  placeholder="Copiez-collez ici le contenu de votre CV..."
                  className="w-full h-40 p-3 border border-zinc-200 rounded-xl text-sm text-zinc-700 resize-none focus:outline-none focus:border-blue-400"
                />
              )}

              {error && (
                <div className="mt-3 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">{error}</p>
                </div>
              )}

              <Button
                onClick={() => { if (cvText.trim()) { setStep(2); setError(''); } else { setError('Ajoutez votre CV pour continuer.'); } }}
                className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-3"
              >
                Continuer
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}

          {/* Step 2: Job offer */}
          {step === 2 && (
            <div className="bg-white border border-zinc-200 rounded-2xl p-5 mb-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-zinc-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  L'offre d'emploi
                </h2>
                <button onClick={() => setStep(1)} className="text-xs text-blue-600 hover:underline">
                  ← Modifier mon CV
                </button>
              </div>

              <textarea
                value={offerText}
                onChange={(e) => setOfferText(e.target.value)}
                placeholder="Copiez-collez ici le texte complet de l'offre d'emploi (titre du poste, missions, compétences requises...)..."
                className="w-full h-48 p-3 border border-zinc-200 rounded-xl text-sm text-zinc-700 resize-none focus:outline-none focus:border-blue-400"
                autoFocus
              />

              {error && (
                <div className="mt-3 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">{error}</p>
                </div>
              )}

              {loading ? (
                <div className="mt-4 bg-blue-50 border border-blue-100 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-blue-800">
                      {PROGRESS_MSGS[Math.floor((progress / 100) * PROGRESS_MSGS.length)] || PROGRESS_MSGS[PROGRESS_MSGS.length - 1]}
                    </p>
                    <span className="text-sm font-bold text-blue-700 tabular-nums">{progress}%</span>
                  </div>
                  <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-600 rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="text-xs text-blue-500 mt-2 text-center">Ne fermez pas cette page...</p>
                </div>
              ) : (
                <>
                  <Button
                    onClick={handleAnalyse}
                    className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-4 text-base font-semibold shadow-lg shadow-blue-600/25"
                  >
                    <Zap className="h-5 w-5 mr-2" />
                    Lancer l'analyse
                  </Button>
                  <p className="text-xs text-zinc-400 text-center mt-2">
                    L'analyse est gratuite — paiement uniquement pour débloquer le rapport complet
                  </p>
                </>
              )}
            </div>
          )}

          {/* Trust badges */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-center gap-2 text-xs text-zinc-500">
              <Shield className="h-3.5 w-3.5 text-green-500" />
              L'IA n'invente aucune expérience ou compétence
            </div>
            <div className="flex items-center justify-center gap-4 text-xs text-zinc-500">
              <Link to="/connexion" className="text-blue-600 hover:underline font-medium">
                Se connecter
              </Link>
              <span className="text-zinc-300">·</span>
              <Link to="/inscription" className="text-blue-600 hover:underline font-medium">
                Créer un compte
              </Link>
            </div>
            <p className="text-center text-xs text-zinc-400">
              Un compte vous permet de retrouver vos analyses et accéder à des analyses illimitées
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyserPage;
